import { navigateTo } from 'nuxt/app'

import type { CPNavigationInput } from '@common/navigation-layer/types/navigation'

import type { ProfileMenuNavigation } from '../constants/profile-menu'

/** Minimal shape of `CPNavigateTo` from `useCPNavigation()` for profile menu links. */
export type ProfileMenuCPNavigateTo = (input: CPNavigationInput) => Promise<unknown>

/**
 * Runs navigation for a profile menu item: internal routes use CPNavigateTo; external paths
 * use `navigateTo` or `window.open` when opening in a new tab.
 *
 * @returns `false` when opening in a new tab was blocked (e.g. popup blocker); `true` otherwise.
 */
export async function executeProfileMenuNavigation(nav: ProfileMenuNavigation, CPNavigateTo: ProfileMenuCPNavigateTo): Promise<boolean> {
  if (nav.type === 'internal') {
    const options = nav.newTab ? { open: { target: '_blank' as const } } : undefined
    await CPNavigateTo({ name: nav.name, query: nav.query, options })
    return true
  }
  if (nav.newTab && typeof window !== 'undefined') {
    const href =
      nav.path.startsWith('http://') || nav.path.startsWith('https://') ? nav.path : new URL(nav.path, window.location.origin).href
    const opened = window.open(href, '_blank', 'noopener,noreferrer')
    if (!opened) {
      return false
    }
    opened.opener = null
    return true
  }
  await navigateTo(nav.path, { external: true })
  return true
}
